/** A canonical International Morse Code element. */
export type MorseElement = '.' | '-'

/** The result of classifying a straight-key press. */
export type KeyPressKind = 'dit' | 'dah'

/**
 * International Morse Code for the Latin alphabet, digits, and commonly used
 * punctuation. Values deliberately use ASCII `.` and `-` so they can also be
 * sent by keyboard/HID input without any display-specific conversion.
 */
export const MORSE_CODE = Object.freeze({
  A: '.-',
  B: '-...',
  C: '-.-.',
  D: '-..',
  E: '.',
  F: '..-.',
  G: '--.',
  H: '....',
  I: '..',
  J: '.---',
  K: '-.-',
  L: '.-..',
  M: '--',
  N: '-.',
  O: '---',
  P: '.--.',
  Q: '--.-',
  R: '.-.',
  S: '...',
  T: '-',
  U: '..-',
  V: '...-',
  W: '.--',
  X: '-..-',
  Y: '-.--',
  Z: '--..',
  '0': '-----',
  '1': '.----',
  '2': '..---',
  '3': '...--',
  '4': '....-',
  '5': '.....',
  '6': '-....',
  '7': '--...',
  '8': '---..',
  '9': '----.',
  '.': '.-.-.-',
  ',': '--..--',
  '?': '..--..',
  "'": '.----.',
  '!': '-.-.--',
  '/': '-..-.',
  '(': '-.--.',
  ')': '-.--.-',
  '&': '.-...',
  ':': '---...',
  ';': '-.-.-.',
  '=': '-...-',
  '+': '.-.-.',
  '-': '-....-',
  _: '..--.-',
  '"': '.-..-.',
  $: '...-..-',
  '@': '.--.-.',
} as const)

export type SupportedMorseCharacter = keyof typeof MORSE_CODE
export type MorsePattern = (typeof MORSE_CODE)[SupportedMorseCharacter]

/** Reverse lookup table. All entries in the International table are unique. */
export const MORSE_CHARACTER = Object.freeze(
  Object.fromEntries(
    Object.entries(MORSE_CODE).map(([character, pattern]) => [pattern, character]),
  ),
) as Readonly<Record<MorsePattern, SupportedMorseCharacter>>

/** Alias that reads naturally at call sites that perform a reverse lookup. */
export const REVERSE_MORSE_CODE = MORSE_CHARACTER

const DISPLAY_DITS = new Set(['.', '·', '•'])
const DISPLAY_DAHS = new Set(['-', '−', '–', '—'])

function isOwnMorseCharacter(value: string): value is SupportedMorseCharacter {
  return Object.prototype.hasOwnProperty.call(MORSE_CODE, value)
}

function isOwnMorsePattern(value: string): value is MorsePattern {
  return Object.prototype.hasOwnProperty.call(MORSE_CHARACTER, value)
}

function assertFinitePositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a finite number greater than 0`)
  }
}

function normalizeElements(value: string, ignoreWhitespace: boolean): string | undefined {
  let result = ''

  for (const character of value.trim()) {
    if (DISPLAY_DITS.has(character)) {
      result += '.'
    } else if (DISPLAY_DAHS.has(character)) {
      result += '-'
    } else if (ignoreWhitespace && /\s/u.test(character)) {
      continue
    } else {
      return undefined
    }
  }

  return result.length > 0 ? result : undefined
}

/** Returns true only for a canonical, known International Morse pattern. */
export function isMorsePattern(value: string): value is MorsePattern {
  return isOwnMorsePattern(value)
}

/** Encode exactly one supported character. ASCII letters are case-insensitive. */
export function encodeMorseCharacter(character: string): MorsePattern | undefined {
  const codePoints = Array.from(character)
  if (codePoints.length !== 1) return undefined

  const normalized = codePoints[0].toUpperCase()
  return isOwnMorseCharacter(normalized) ? MORSE_CODE[normalized] : undefined
}

/**
 * Decode one Morse character. Besides canonical ASCII symbols, common display
 * glyphs (`·`, `•`, `−`, `–`, `—`) and spaces between elements are accepted.
 */
export function decodeMorseCharacter(pattern: string): SupportedMorseCharacter | undefined {
  const normalized = normalizeElements(pattern, true)
  return normalized !== undefined && isOwnMorsePattern(normalized)
    ? MORSE_CHARACTER[normalized]
    : undefined
}

export type UnknownCharacterBehavior = 'throw' | 'skip' | 'replace'

export interface EncodeMorseOptions {
  /** Separator between encoded characters. Defaults to one space. */
  letterSeparator?: string
  /** Separator between encoded words. Defaults to ` / `. */
  wordSeparator?: string
  /** What to do with unsupported input. Defaults to `throw`. */
  onUnknownCharacter?: UnknownCharacterBehavior
  /** Pattern used by `replace`. Defaults to the question-mark pattern. */
  replacementPattern?: string
}

/**
 * Encode text as Morse. Runs of Unicode whitespace are treated as a single word
 * boundary. Unsupported characters throw by default so practice content cannot
 * be silently corrupted.
 */
export function encodeMorse(text: string, options: EncodeMorseOptions = {}): string {
  const {
    letterSeparator = ' ',
    wordSeparator = ' / ',
    onUnknownCharacter = 'throw',
    replacementPattern = MORSE_CODE['?'],
  } = options

  const normalizedReplacement = normalizeElements(replacementPattern, true)
  if (onUnknownCharacter === 'replace' && normalizedReplacement === undefined) {
    throw new RangeError('replacementPattern must contain at least one dit or dah')
  }

  const trimmed = text.trim()
  if (trimmed.length === 0) return ''

  const encodedWords: string[] = []
  for (const word of trimmed.split(/\s+/u)) {
    const encodedCharacters: string[] = []

    for (const character of Array.from(word)) {
      const encoded = encodeMorseCharacter(character)
      if (encoded !== undefined) {
        encodedCharacters.push(encoded)
        continue
      }

      switch (onUnknownCharacter) {
        case 'skip':
          break
        case 'replace':
          // The option validation above guarantees this in this branch.
          encodedCharacters.push(normalizedReplacement as string)
          break
        case 'throw':
          throw new RangeError(`Unsupported Morse character: ${JSON.stringify(character)}`)
        default: {
          const exhaustiveCheck: never = onUnknownCharacter
          throw new TypeError(`Unknown character behavior: ${String(exhaustiveCheck)}`)
        }
      }
    }

    if (encodedCharacters.length > 0) {
      encodedWords.push(encodedCharacters.join(letterSeparator))
    }
  }

  return encodedWords.join(wordSeparator)
}

export type UnknownPatternBehavior = 'undefined' | 'throw' | 'skip' | 'replace'

export interface DecodeMorseOptions {
  /** What to do with an unknown or malformed pattern. Defaults to `undefined`. */
  onUnknownPattern?: UnknownPatternBehavior
  /** Character used by `replace`. Defaults to the Unicode replacement character. */
  replacementCharacter?: string
}

interface ParsedMorseText {
  words: string[][]
}

/**
 * Parse conventional text Morse: whitespace separates letters, while `/`, `|`,
 * or two-or-more whitespace characters separate words.
 */
function parseMorseText(value: string): ParsedMorseText | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) return { words: [] }

  const slashSeparated = trimmed.split(/\s*[\/|]\s*/u)
  if (slashSeparated.some((part) => part.length === 0)) return undefined

  const rawWords = slashSeparated.flatMap((part) => part.split(/\s{2,}/u))
  if (rawWords.some((word) => word.length === 0)) return undefined

  const words: string[][] = []
  for (const rawWord of rawWords) {
    const patterns = rawWord.trim().split(/\s/u)
    if (patterns.some((pattern) => pattern.length === 0)) return undefined
    words.push(patterns)
  }

  return { words }
}

/**
 * Decode a Morse character, word, or sentence. Returns `undefined` when any
 * pattern is unknown/malformed unless another behavior is explicitly selected.
 */
export function decodeMorse(
  sequence: string,
  options: DecodeMorseOptions = {},
): string | undefined {
  const {
    onUnknownPattern = 'undefined',
    replacementCharacter = '\uFFFD',
  } = options
  const parsed = parseMorseText(sequence)

  if (parsed === undefined) {
    if (onUnknownPattern === 'throw') {
      throw new RangeError('Malformed Morse sequence')
    }
    if (onUnknownPattern === 'replace') return replacementCharacter
    return onUnknownPattern === 'skip' ? '' : undefined
  }

  const decodedWords: string[] = []
  for (const word of parsed.words) {
    let decodedWord = ''

    for (const rawPattern of word) {
      const normalized = normalizeElements(rawPattern, false)
      const decoded =
        normalized !== undefined && isOwnMorsePattern(normalized)
          ? MORSE_CHARACTER[normalized]
          : undefined

      if (decoded !== undefined) {
        decodedWord += decoded
        continue
      }

      switch (onUnknownPattern) {
        case 'skip':
          break
        case 'replace':
          decodedWord += replacementCharacter
          break
        case 'throw':
          throw new RangeError(`Unknown Morse pattern: ${JSON.stringify(rawPattern)}`)
        case 'undefined':
          return undefined
        default: {
          const exhaustiveCheck: never = onUnknownPattern
          throw new TypeError(`Unknown pattern behavior: ${String(exhaustiveCheck)}`)
        }
      }
    }

    if (decodedWord.length > 0) decodedWords.push(decodedWord)
  }

  return decodedWords.join(' ')
}

/** Standard PARIS timing: one dit lasts 1,200 / WPM milliseconds. */
export function wpmToDotDuration(wpm: number): number {
  assertFinitePositive(wpm, 'wpm')
  return 1200 / wpm
}

/** Short alias useful in timing-sensitive input code. */
export const wpmToDotMs = wpmToDotDuration

/**
 * Classify a straight-key press using the midpoint between an ideal dit (1 unit)
 * and dah (3 units). A press exactly on the threshold is a dah.
 */
export function classifyKeyPress(
  durationMs: number,
  dotDurationMs: number,
  dahThresholdInDots = 2,
): KeyPressKind {
  if (!Number.isFinite(durationMs) || durationMs < 0) {
    throw new RangeError('durationMs must be a finite number greater than or equal to 0')
  }
  assertFinitePositive(dotDurationMs, 'dotDurationMs')
  assertFinitePositive(dahThresholdInDots, 'dahThresholdInDots')

  return durationMs >= dotDurationMs * dahThresholdInDots ? 'dah' : 'dit'
}

/** Classify a press and return the canonical symbol expected by the decoder. */
export function classifyKeyPressAsElement(
  durationMs: number,
  dotDurationMs: number,
  dahThresholdInDots = 2,
): MorseElement {
  return classifyKeyPress(durationMs, dotDurationMs, dahThresholdInDots) === 'dit' ? '.' : '-'
}

/** Compatibility alias with a more timing-oriented name. */
export const classifyPressDuration = classifyKeyPress

export interface MorseDisplayOptions {
  /** Glyph for a dit. Defaults to `·`. */
  dit?: string
  /** Glyph for a dah. Defaults to `−`. */
  dah?: string
  /** Gap between elements of one character. Defaults to one space. */
  elementSeparator?: string
  /** Gap between characters. Defaults to three spaces. */
  letterSeparator?: string
  /** Gap between words. Defaults to seven spaces. */
  wordSeparator?: string
}

const DEFAULT_DISPLAY_OPTIONS: Required<MorseDisplayOptions> = Object.freeze({
  dit: '·',
  dah: '−',
  elementSeparator: ' ',
  letterSeparator: '   ',
  wordSeparator: '       ',
})

/** Format one sequence of dits/dahs for visual presentation. */
export function formatMorsePattern(
  pattern: string,
  options: Pick<MorseDisplayOptions, 'dit' | 'dah' | 'elementSeparator'> = {},
): string | undefined {
  const normalized = normalizeElements(pattern, true)
  if (normalized === undefined) return undefined

  const dit = options.dit ?? DEFAULT_DISPLAY_OPTIONS.dit
  const dah = options.dah ?? DEFAULT_DISPLAY_OPTIONS.dah
  const elementSeparator =
    options.elementSeparator ?? DEFAULT_DISPLAY_OPTIONS.elementSeparator

  return Array.from(normalized, (element) => (element === '.' ? dit : dah)).join(
    elementSeparator,
  )
}

/**
 * Format conventional text Morse with readable one/three/seven-unit spacing.
 * Unlike decoding, syntactically valid but currently unmapped patterns can still
 * be displayed.
 */
export function morseToDisplay(
  sequence: string,
  options: MorseDisplayOptions = {},
): string | undefined {
  const parsed = parseMorseText(sequence)
  if (parsed === undefined) return undefined

  const resolvedOptions = { ...DEFAULT_DISPLAY_OPTIONS, ...options }
  const displayedWords: string[] = []

  for (const word of parsed.words) {
    const displayedCharacters: string[] = []
    for (const pattern of word) {
      const displayed = formatMorsePattern(pattern, resolvedOptions)
      if (displayed === undefined) return undefined
      displayedCharacters.push(displayed)
    }
    displayedWords.push(displayedCharacters.join(resolvedOptions.letterSeparator))
  }

  return displayedWords.join(resolvedOptions.wordSeparator)
}

/** Alias that is convenient in JSX render helpers. */
export const formatMorseForDisplay = morseToDisplay
